import React, { useMemo } from 'react'
import { BarChart3, DollarSign, Building2, TrendingUp } from 'lucide-react'
import { useData, useDashboardMetrics, transformToTimeline, groupByQuarter } from '../hooks/useApi'
import { transformUpdates, transformFollowOns, calculateTotalInvestments } from '../utils/dashboardTransformers'
import AccountOverview from '../components/dashboard/AccountOverview'
import PortfolioUpdates from '../components/dashboard/PortfolioUpdates'
import FollowOnOpportunities from '../components/dashboard/FollowOnOpportunities'
import InvestmentTimeline from '../components/dashboard/InvestmentTimeline'
import LoadingSpinner from '../components/shared/LoadingSpinner'

/**
 * Dashboard page component - main investment dashboard
 * Displays account overview, investments, updates, and opportunities
 * Mobile-first responsive design with adaptive layouts
 * Uses standardized data fetching patterns
 */
const DashboardPage = () => {
  // Single hook with parallel fetching - replaces useDashboardData
  const { data, loading, error, refetch } = useData('dashboard')
  
  // Destructure the parallel-fetched data
  const { metrics: metricsData, investments: investmentsData, updates: updatesData, followOns: followOnsData } = data || {}

  // Use dashboard metrics hook for metrics transformation
  const { getMetrics } = useDashboardMetrics()

  // Get metrics with automatic transformation and loading states
  const accountOverview = getMetrics(metricsData, loading, [DollarSign, Building2, TrendingUp])
  
  // Transform investment data to timeline format
  const timelineData = transformToTimeline(investmentsData)
  const timelineGroups = groupByQuarter(timelineData)

  // Calculate total investments for portfolio allocation chart
  const totalInvestments = calculateTotalInvestments(timelineData)

  // Transform API data using utility functions
  const latestUpdates = useMemo(() => transformUpdates(updatesData), [updatesData])
  const followOnOpportunities = useMemo(() => transformFollowOns(followOnsData), [followOnsData])

  // Handle retry on error - single refetch call
  const handleRetry = () => {
    refetch()
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-4 xl:p-8">
        <LoadingSpinner 
          size="large" 
          text="Loading dashboard..." 
          fullScreen 
        />
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-4 xl:p-8">
        <div className="text-center py-8">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-gray-600 text-lg mb-4">Failed to load dashboard data</p>
          <p className="text-gray-500 mb-6">Please try again or contact support if the problem persists.</p>
          <button 
            onClick={handleRetry}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-4 xl:p-8">
      {/* Dashboard Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Investment Dashboard
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                Welcome back! Here's your investment overview
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        {/* <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search investments, companies, or deals..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
          </div>
          <div className="flex gap-2">
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>All Industries</option>
              <option>Technology</option>
              <option>Clean Energy</option>
              <option>Fintech</option>
              <option>Data Analytics</option>
            </select>
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>All Statuses</option>
              <option>Active</option>
              <option>Pending</option>
              <option>Completed</option>
            </select>
            <Button variant="primary" size="medium">
              Filter
            </Button>
          </div>
        </div> */}
      </div>

      {/* Account Overview Section - Mobile & Tablet Only */}
      <AccountOverview 
        accountOverview={accountOverview} 
        loading={loading} 
        className="lg:hidden"
        style="grid"
      />



      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-4 xl:gap-8">
        {/* Left Column - Investments and Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Investment Performance Chart */}
          {/* <section>
            <Chart
              title="Investment Performance"
              subtitle="Portfolio value over time"
              type="line"
              height="h-64"
              data={[
                { label: 'TechStart Inc.', color: '#3B82F6' },
                { label: 'GreenTech Solutions', color: '#10B981' },
                { label: 'FinanceFlow', color: '#8B5CF6' },
                { label: 'DataDrive Analytics', color: '#F59E0B' }
              ]}
            />
          </section> */}

          {/* Latest Updates */}
          <PortfolioUpdates 
            latestUpdates={latestUpdates} 
            loading={loading} 
            pageSize={3}
          />

           {/* Follow-on Opportunities */}
           <FollowOnOpportunities 
             followOnOpportunities={followOnOpportunities} 
             loading={loading} 
           />

          
        </div>

        {/* Right Column - Updates, Charts and Opportunities */}
        <div className="space-y-6">
          
          {/* Desktop Account Overview - Horizontal Stack */}
          <AccountOverview 
            accountOverview={accountOverview} 
            loading={loading} 
            className="md:hidden"
            style="icon" 
          />

          {/* Portfolio Allocation Chart */}
          {/* <section>
            <Chart
              title="Portfolio Allocation"
              subtitle="Investment distribution by industry"
              type="pie"
              height="h-64"
              data={(() => {
                // Calculate industry totals from investments data
                const industryTotals = investments.reduce((acc, investment) => {
                  const industry = investment.industry
                  const amount = parseFloat(investment.total.replace(/[^0-9.]/g, '')) // Extract numeric value
                  acc[industry] = (acc[industry] || 0) + amount
                  return acc
                }, {})

                const chartData = Object.entries(industryTotals).map(([industry, total]) => ({
                  label: industry,
                  value: total
                }))

                return chartData
              })()}
            />
          </section> */}
         
        </div>
        
      </div>

        {/* Investment Timeline */}
        <InvestmentTimeline 
          timelineData={timelineData}
          timelineGroups={timelineGroups}
          loading={loading}
        />
    </div>
  )
}

export default DashboardPage
